import React, { useState, useEffect } from "react";
import Button from "../../../components/common/button";
import Dropdown from "../../../components/common/dropdown";
import { useOrganization } from "../../../hooks/useOrganization";
import industryOptsData from '../common/industryOpts.json';
import ErrorPopup from "../../../components/common/errorPopup";
import OnboardingCard from "../../../components/common/OnboardingCard";
import PreviewCard from "../../../components/common/PreviewCard";

interface CompanyFormProps {
  onNext: () => void;
  onBack: () => void;
  currentStep: number;
  totalSteps: number;
  initialData: {
    companyName: string;
    companySize: string;
    industry: string;
    industry_other: string;
    website: string;
  };
}

const CompanyForm: React.FC<CompanyFormProps> = ({ 
  onNext, 
  onBack, 
  currentStep, 
  totalSteps,
  initialData
}) => {
  const { organization, updateOrganization, isUpdating } = useOrganization();
  const [formData, setFormData] = useState({
    companyName: initialData.companyName || '',
    companySize: initialData.companySize || '',
    industry: initialData.industry || '',
    industry_other: initialData.industry_other || '',
    website: initialData.website || '',
  });
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchMissingData = async () => {
      try {
        let updatedFormData = { ...formData };
        
        if (!initialData.companyName && organization?.linkedin_company_profile?.name) {
          updatedFormData.companyName = organization.linkedin_company_profile.name;
        }
        if (!initialData.companySize && organization?.linkedin_company_profile?.size) {
          updatedFormData.companySize = organization.linkedin_company_profile.size;
        }
        if (!initialData.industry && organization?.linkedin_company_profile?.industry) {
          const industryValue = organization.linkedin_company_profile.industry;
          const industryExists = industryOptsData.industries.some(
            ind => ind.toLowerCase() === industryValue.toLowerCase()
          );
          updatedFormData.industry = industryExists ? industryValue : 'other';
          updatedFormData.industry_other = !industryExists ? industryValue : '';
        }
        if (!initialData.website && organization?.linkedin_company_profile?.company_website) {
          updatedFormData.website = organization.linkedin_company_profile.company_website;
        }
        setFormData(updatedFormData);
      } catch (error) {
        console.error('Error fetching missing data:', error);
      }
    };

    if (!initialData.companyName || !initialData.companySize || !initialData.industry || !initialData.website) {
      fetchMissingData();
    }
  }, [initialData, organization]);

  useEffect(() => {
    const refreshCompanyData = async () => {
      try {
        // Fetch fresh organization data
        const updatedOrg = await updateOrganization({});
        
        if (updatedOrg?.linkedin_company_profile) {
          const profile = updatedOrg.linkedin_company_profile;
          const industryValue = profile.industry || '';
          const industryExists = industryOptsData.industries.some(
            ind => ind.toLowerCase() === industryValue.toLowerCase()
          );

          setFormData({
            companyName: profile.name || '',
            companySize: profile.size || '',
            industry: industryExists ? industryValue : 'other',
            industry_other: !industryExists ? industryValue : '',
            website: profile.company_website || '',
          });
        }
      } catch (error) {
        console.error('Error refreshing company data:', error);
      }
    };

    // Call refresh when component mounts
    refreshCompanyData();
  }, []); // Empty dependency array to run only on mount

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const isFormValid = () => {
    const requiredFields = ['companyName', 'companySize', 'industry', 'website'] as const;
    const isMainFieldsValid = requiredFields.every(field => formData[field as keyof typeof formData].trim() !== '');
    return formData.industry === 'other' ? isMainFieldsValid && formData.industry_other.trim() !== '' : isMainFieldsValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid()) {
      try {
        setError('');
        const industryValue = formData.industry === 'other' ? formData.industry_other : formData.industry;
        
        await updateOrganization({
          linkedin_company_profile: {
            ...organization?.linkedin_company_profile,
            name: formData.companyName || '',
            size: formData.companySize || '',
            industry: industryValue || '',
            company_website: formData.website || '',
          }
        });
        
        onNext();
      } catch (error) {
        console.error('Failed to update company details:', error);
        setErrorMessage('Failed to update company details. Please try again.');
        setShowError(true);
      }
    }
  };

  const companySizeOptions = [
    { value: '0-1', label: '0-1 employees' },
    { value: '2-10', label: '2-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-500', label: '201-500 employees' },
    { value: '501-1000', label: '501-1,000 employees' },
    { value: '1001-5000', label: '1,001-5,000 employees' },
    { value: '5001-10000', label: '5,001-10,000 employees' },
    { value: '10001+', label: '10,001+ employees' }
  ];

  const industryOptions = industryOptsData.industries.map(industry => ({
    value: industry,
    label: industry
  }));

  const leftPanelContent = (
    <div className="flex flex-col h-full">
      <div className="flex flex-col px-[16px] pt-[4px] pb-[16px]">
        <div className="mb-1">
          <div className="text-[32px] font-semibold mb-[36px] text-left">
            Let’s get to know your business better! 
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="space-y-3">
              {/* Company Name */}
              <div className="relative">
                <label className="text-sm font-normal text-[#75777C] mb-2 block text-left">
                  Company's name
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder="Enter company name"
                  className="w-full h-[40px] pl-4 pr-4 text-left rounded-[10px] text-[#222222] text-sm placeholder:text-[#9FA1A7] focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent shadow-[0px_0px_0px_1px_#EEEFF1_inset]"
                  required
                />
              </div>

              {/* Company Size */}
              <div className="relative">
                <label className="text-sm font-normal text-[#75777C] mb-2 block text-left">
                  Company's size
                </label>
                <Dropdown
                  name="companySize"
                  value={formData.companySize}
                  onChange={handleInputChange}
                  options={companySizeOptions}
                  placeholder="Select company size"
                  className="w-full h-[40px] pl-4 pr-4 text-left rounded-[10px] text-[#222222] text-sm placeholder:text-[#9FA1A7] focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent shadow-[0px_0px_2px_0px_#EEEFF1]"
                />
              </div>

              {/* Industry */}
              <div className="relative">
                <label className="text-sm font-normal text-[#75777C] mb-2 block text-left">
                  Company's Industry
                </label>
                <Dropdown
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                  options={industryOptions}
                  placeholder="Select industry"
                  otherValue={formData.industry_other}
                  allowOther={true}
                  onOtherChange={(value) => {
                    setFormData(prev => ({
                      ...prev,
                      industry_other: value
                    }));
                  }}
                  className="w-full h-[40px] pl-4 pr-4 text-left rounded-[10px] text-[#222222] text-sm placeholder:text-[#9FA1A7] focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent shadow-[0px_0px_2px_0px_#EEEFF1]"
                />
              </div>

              {/* Website */}
              <div className="relative">
                <label className="text-sm font-normal text-[#75777C] mb-2 block text-left">
                  Company's website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="Share your website URL"
                  className="w-full h-[40px] pl-4 pr-4 text-left rounded-[10px] text-[#222222] text-sm placeholder:text-[#9FA1A7] focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent shadow-[0px_0px_0px_1px_#EEEFF1_inset]"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full h-[40px] bg-[#29DC5B] mt-[28px] text-[#222222] rounded-[10px] flex items-center justify-center text-sm font-normal hover:bg-[#22c55e] transition-colors shadow-[0px_0px_0px_1px_#2325291A_inset]"
              disabled={isUpdating || !isFormValid()}
            >
              {isUpdating ? 'Updating...' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    const handleKeyDown = async (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        try {
          const industryValue = formData.industry === 'other' ? formData.industry_other : formData.industry;
          
          // Only update if there are changes
          const hasChanges = 
            formData.companyName !== initialData.companyName ||
            formData.companySize !== initialData.companySize ||
            industryValue !== initialData.industry ||
            formData.website !== initialData.website;

          if (hasChanges) {
            await updateOrganization({
              linkedin_company_profile: {
                ...organization?.linkedin_company_profile,
                name: formData.companyName || '',
                size: formData.companySize || '',
                industry: industryValue || '',
                company_website: formData.website || '',
              }
            });
          }
        } catch (error) {
          console.error('Failed to save company details on back:', error);
          setErrorMessage('Failed to save changes. Your progress may be lost.');
          setShowError(true);
          // Still navigate back even if save fails
        } finally {
          onBack();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onBack, formData, updateOrganization, organization, initialData]);

  return (
    <div className="min-h-screen h-screen flex flex-col items-center">
      <OnboardingCard 
        leftPanel={leftPanelContent}
        rightPanel={<PreviewCard 
          variant="preview" 
          companyName={formData.companyName || 'Workspace title'}
          showRole={false}
        />}
        pageInfo={{
          currentPage: currentStep + 1,
          totalPages: totalSteps
        }}
      />
      
      <ErrorPopup
        isOpen={showError}
        onClose={() => setShowError(false)}
        message={errorMessage}
      />
    </div>
  );
};

export default CompanyForm;
